import { ApolloLink, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { ApolloClient, InMemoryCache } from '@apollo/client-integration-nextjs';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { GraphQLError } from 'graphql';

import { CONFIG } from '@/config';
import { STATUS_CODES } from '@/constants/common';
import { logGraphQLErrors } from '@/utils/apollo/log-graphql-error';
import { CookiesUtils } from '@/utils/helpers/cookies';
import { hostname } from '@/utils/helpers/locales';

const { SUCCESS, AUTHENTICATION_ERROR } = STATUS_CODES;

export function makeClient(locale: string, ssrCookie?: string) {
  const uploadLink = createUploadLink({
    uri: `https://${hostname(locale, CONFIG.host)}/graphql`,
    headers: {
      'Apollo-Require-Preflight': 'true'
    },
    credentials: 'include'
  });

  const statusMiddleware = new ApolloLink((operation, forward) =>
    forward(operation).map(response => {
      const data = response?.data;
      const isMutation = operation.query.definitions.some(
        def =>
          def.kind === 'OperationDefinition' && def.operation === 'mutation'
      );

      if (data && isMutation) {
        Object.keys(data).forEach(key => {
          const result = data[key];
          if (result?.status && result.status !== SUCCESS) {
            response.errors = [
              new GraphQLError(
                `Mutation failed with status: ${result.status}`,
                {
                  extensions: {
                    code: result.status,
                    lockExpiresAt: result?.lockExpiresAt,
                    isStatusError: true
                  }
                }
              )
            ];
            response.data = null;
          }
        });
      }

      return response;
    })
  );

  // TODO: Replace with new error format after upgrade to Apollo Client 4.0
  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      const authError = graphQLErrors.some(
        error => error.extensions?.code === AUTHENTICATION_ERROR
      );

      if (authError) {
        CookiesUtils.logout();
      }

      logGraphQLErrors(graphQLErrors);
    }

    if (networkError) console.error(`[Network error]: ${networkError}`);
  });

  const authLink = setContext((_, { headers }) => {
    const cookie = ssrCookie || headers?.cookie;

    return {
      headers: {
        ...headers,
        ...(cookie ? { cookie } : {})
      }
    };
  });

  const operationNameLink = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        'X-APOLLO-OPERATION-NAME': operation.operationName || ''
      }
    }));

    return forward(operation);
  });

  return new ApolloClient({
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            profile: {
              merge: true
            },
            ads: {
              keyArgs: ['state']
            },
            vacancies: {
              merge: true
            },
            resumes: {
              merge: true
            }
          }
        }
      }
    }),
    link: from([
      errorLink,
      statusMiddleware,
      authLink,
      operationNameLink,
      uploadLink
    ])
  });
}
